// What the release view derives from release health (D72): the scope's latest release and the
// one before it, the comparison between them, adoption and a daily crash-free series.
import type { ReleaseHealth, ReleaseHealthDay, ReleaseVitals } from '../api/types'

/** One app's releases, newest first by when each started (released_at, APS-441). */
export function releasesNewestFirst(rows: ReleaseHealth[], appId?: string): ReleaseHealth[] {
  return rows
    .filter((r) => !appId || r.app_id === appId)
    .slice()
    .sort(
      (a, b) => b.released_at.localeCompare(a.released_at) || b.release.localeCompare(a.release),
    )
}

/** The app with the most sessions in the window: the default scope when none is chosen. */
export function busiestApp(rows: ReleaseHealth[]): string | undefined {
  const sessions = new Map<string, number>()
  for (const r of rows) sessions.set(r.app_id, (sessions.get(r.app_id) ?? 0) + r.sessions)
  let best: string | undefined
  let most = -1
  for (const [app, n] of sessions)
    if (n > most) {
      best = app
      most = n
    }
  return best
}

export type Verdict = 'worse' | 'better' | 'same' | 'only'

/** Crash-free rates within a hundredth of a point read as the same. */
const SAME = 0.0001

export interface Comparison {
  current: ReleaseHealth
  previous: ReleaseHealth | undefined
  verdict: Verdict
  /** Change in crash-free sessions, in percentage points (−0.33 means 0.33 points worse). */
  sessionsDelta: number | undefined
  usersDelta: number | undefined
}

/**
 * The change from the release before to this one. `change` is the API's comparison over the
 * same time range (D79), b minus a; without it the two releases' own windows are compared.
 */
export function compare(
  current: ReleaseHealth,
  previous?: ReleaseHealth,
  change?: { crash_free_sessions_rate: number | null; crash_free_users_rate: number | null },
): Comparison {
  if (!previous)
    return { current, previous, verdict: 'only', sessionsDelta: undefined, usersDelta: undefined }
  const local = current.crash_free_sessions_rate - previous.crash_free_sessions_rate
  const localUsers =
    current.crash_free_users_rate !== null && previous.crash_free_users_rate !== null
      ? current.crash_free_users_rate - previous.crash_free_users_rate
      : undefined
  const d = change ? (change.crash_free_sessions_rate ?? local) : local
  const du = change ? (change.crash_free_users_rate ?? undefined) : localUsers
  return {
    current,
    previous,
    verdict: Math.abs(d) < SAME ? 'same' : d < 0 ? 'worse' : 'better',
    sessionsDelta: d * 100,
    usersDelta: du === undefined ? undefined : du * 100,
  }
}

/** The release that follows `release` in the list's order (older), if any. */
export function previousOf(list: ReleaseHealth[], release: string): ReleaseHealth | undefined {
  const i = list.findIndex((r) => r.release === release)
  return i >= 0 ? list[i + 1] : undefined
}

/** Share of sessions per release, largest first. */
export function adoption(rows: ReleaseHealth[]): { release: string; share: number }[] {
  const total = rows.reduce((n, r) => n + r.sessions, 0)
  if (total === 0) return []
  return rows
    .map((r) => ({ release: r.release, share: r.sessions / total }))
    .sort((a, b) => b.share - a.share)
}

export const sum = (rows: ReleaseHealth[], key: 'sessions' | 'crashed_sessions'): number =>
  rows.reduce((n, r) => n + r[key], 0)

// ---- Console D (APS-307): the rail, the comparison and the charts since a release shipped ----

/** A release's state on the rail: against the release before it (D103 #14: no "phasing out"). */
export type RailState = 'worse' | 'better' | 'same' | 'first'

export function railState(r: ReleaseHealth, older: ReleaseHealth | undefined): RailState {
  if (!older) return 'first'
  const v = compare(r, older).verdict
  return v === 'only' ? 'first' : v
}

/** "5.2 (812)" reads as version 5.2, build 812; a release without that form has no build. */
export function versionBuild(release: string): { version: string; build: string | undefined } {
  const m = /^(.+?)\s*\(([^()\s]+)\)$/.exec(release)
  return m ? { version: m[1] as string, build: m[2] } : { version: release, build: undefined }
}

/** The CLI command that prints the same comparison (docs/site/cli.mdx). */
export function cliCompare(base: string, candidate: string, appId?: string): string {
  const q = (v: string) => `"${v.replace(/(["\\$`])/g, '\\$1')}"`
  return `apsio releases compare ${q(base)} ${q(candidate)}${appId ? ` --app ${appId}` : ''}`
}

/**
 * Which way a signal is better: `up` for crash-free rates and resolved issues, `down` for the
 * rest. `tone` is the colour of a change for the worse: crash or degraded.
 */
export interface SignalShape {
  better: 'up' | 'down'
  tone: 'crash' | 'warn'
  /** Changes smaller than this read as no change (in the signal's own unit). */
  eps?: number
  /** The bad quantity is 1 − value (crash-free rates), for the relative change. */
  complement?: boolean
}

/** Where a change sits on the delta bar: the side, the bar's share of its half, and a break. */
export interface DeltaBar {
  worse: boolean
  /** 0 to 1 of the half bar; at least a sliver for any change. */
  width: number
  /** The relative change is past the cap, or the base was zero. */
  over: boolean
  /** The relative change of the bad quantity, Infinity from zero. */
  relative: number
}

/** The delta bar ends at a 200 % relative change; beyond, it shows a break. */
export const DELTA_CAP = 2

/**
 * The relative change of a signal's bad quantity from `base` to `candidate` (the crash rate for a
 * crash-free rate), or undefined when either is missing or the change is within `eps`.
 */
export function deltaBar(
  s: SignalShape,
  base: number | null | undefined,
  candidate: number | null | undefined,
): DeltaBar | undefined {
  if (base == null || candidate == null) return undefined
  const d = candidate - base
  if (Math.abs(d) <= (s.eps ?? 1e-9)) return undefined
  const qb = s.complement ? 1 - base : base
  const qc = s.complement ? 1 - candidate : candidate
  const worse = s.better === 'up' ? d < 0 : d > 0
  const relative = qb > 0 ? Math.abs(qc - qb) / qb : Number.POSITIVE_INFINITY
  return {
    worse,
    width: Math.max(0.06, Math.min(relative, DELTA_CAP) / DELTA_CAP),
    over: relative > DELTA_CAP,
    relative,
  }
}

/** Per 1,000 sessions, or null without sessions. */
export const perThousandSessions = (n: number, sessions: number): number | null =>
  sessions > 0 ? (n / sessions) * 1000 : null

/** The UTC day of an ISO time, `YYYY-MM-DD`. */
const dayOf = (iso: string) => iso.slice(0, 10)

/**
 * Crash-free sessions since a release shipped, cumulative, one point per day from its first day
 * (at most `max` days): day n is 1 − crashed / sessions over days 0 to n. Days before it shipped
 * are skipped; a day with no sessions yet keeps the rate so far (null before the first session).
 */
export function crashFreeSinceShip(
  days: readonly ReleaseHealthDay[],
  firstSeen: string,
  max = 15,
): (number | null)[] {
  const from = dayOf(firstSeen)
  const out: (number | null)[] = []
  let sessions = 0
  let crashed = 0
  for (const d of days) {
    if (dayOf(d.day) < from) continue
    if (out.length >= max) break
    sessions += d.sessions
    crashed += d.crashed_sessions
    out.push(sessions > 0 ? 1 - crashed / sessions : null)
  }
  return out
}

/** A release's share of the app's sessions per day, over the app's days (0 on a day without). */
export function adoptionByDay(
  release: readonly ReleaseHealthDay[],
  app: readonly ReleaseHealthDay[],
): number[] {
  const mine = new Map(release.map((d) => [dayOf(d.day), d.sessions]))
  return app.map((d) => (d.sessions > 0 ? (mine.get(dayOf(d.day)) ?? 0) / d.sessions : 0))
}

/** The comparison's signals (Console D's Releases), in their groups' order. */
export type SignalKey =
  | 'cfs'
  | 'cfu'
  | 'crashedPerK'
  | 'abnormalPerK'
  | 'cold90'
  | 'cold50'
  | 'warm90'
  | 'screen'
  | 'freezePerK'
  | 'endpoint'
  | 'newIssues'
  | 'regressed'
  | 'resolved'

/** How a signal prints: a rate (0 to 1), per 1k sessions, a duration in ms, a count. */
export type SignalUnit = 'rate' | 'perK' | 'ms' | 'count' | 'failed'

export interface Signal extends SignalShape {
  key: SignalKey
  unit: SignalUnit
  group: 'stability' | 'performance' | 'issues'
}

export const SIGNALS: readonly Signal[] = [
  {
    key: 'cfs',
    unit: 'rate',
    group: 'stability',
    better: 'up',
    tone: 'crash',
    complement: true,
    eps: SAME,
  },
  {
    key: 'cfu',
    unit: 'rate',
    group: 'stability',
    better: 'up',
    tone: 'crash',
    complement: true,
    eps: SAME,
  },
  {
    key: 'crashedPerK',
    unit: 'perK',
    group: 'stability',
    better: 'down',
    tone: 'crash',
    eps: 0.05,
  },
  {
    key: 'abnormalPerK',
    unit: 'perK',
    group: 'stability',
    better: 'down',
    tone: 'warn',
    eps: 0.05,
  },
  { key: 'cold90', unit: 'ms', group: 'performance', better: 'down', tone: 'warn', eps: 0.5 },
  { key: 'cold50', unit: 'ms', group: 'performance', better: 'down', tone: 'warn', eps: 0.5 },
  { key: 'warm90', unit: 'ms', group: 'performance', better: 'down', tone: 'warn', eps: 0.5 },
  { key: 'screen', unit: 'ms', group: 'performance', better: 'down', tone: 'warn', eps: 0.5 },
  {
    key: 'freezePerK',
    unit: 'perK',
    group: 'performance',
    better: 'down',
    tone: 'warn',
    eps: 0.005,
  },
  {
    key: 'endpoint',
    unit: 'failed',
    group: 'performance',
    better: 'down',
    tone: 'crash',
    eps: 0.0005,
  },
  { key: 'newIssues', unit: 'count', group: 'issues', better: 'down', tone: 'crash' },
  { key: 'regressed', unit: 'count', group: 'issues', better: 'down', tone: 'warn' },
  { key: 'resolved', unit: 'count', group: 'issues', better: 'up', tone: 'warn' },
]

/** One release's value per signal; null when it has no data for it. */
export type ReleaseFacts = Record<SignalKey, number | null>

/** The issues regressed or resolved in each release, from the issue states (D100). */
export function stateCounts(
  issues: readonly {
    state?: { regressed_release: string | null; resolved_release: string | null } | null
  }[],
  key: 'regressed_release' | 'resolved_release',
): Map<string, number> {
  const out = new Map<string, number>()
  for (const i of issues) {
    const r = i.state?.[key]
    if (r) out.set(r, (out.get(r) ?? 0) + 1)
  }
  return out
}

/**
 * A release's facts from release health and its vitals: crash-free rates, crashed sessions and
 * abnormal exits per 1k, start percentiles, and hangs (or ANRs on Android) per 1k sessions
 * (D103 #14). The screen and endpoint rows depend on the pair, so the caller fills them.
 */
export function releaseFacts(
  r: ReleaseHealth,
  v: ReleaseVitals | undefined,
  android: boolean,
  regressed: ReadonlyMap<string, number> | undefined,
  resolved: ReadonlyMap<string, number> | undefined,
): ReleaseFacts {
  const freeze = v ? (android ? v.hangs.anr_rate : v.hangs.hang_rate) : null
  return {
    cfs: r.crash_free_sessions_rate,
    cfu: r.crash_free_users_rate,
    crashedPerK: perThousandSessions(r.crashed_sessions, r.sessions),
    abnormalPerK: perThousandSessions(r.abnormal_exit_sessions, r.sessions),
    cold90: v?.app_start.cold?.p90_ms ?? null,
    cold50: v?.app_start.cold?.p50_ms ?? null,
    warm90: v?.app_start.warm?.p90_ms ?? null,
    screen: null,
    freezePerK: freeze === null ? null : freeze * 1000,
    endpoint: null,
    newIssues: r.new_issues,
    regressed: regressed ? (regressed.get(r.release) ?? 0) : null,
    resolved: resolved ? (resolved.get(r.release) ?? 0) : null,
  }
}

/**
 * The pair a comparison shows: the candidate (the newer) and its base (an older release). Names
 * that are not in the list fall back to the newest and the release before it.
 */
export function pickPair(
  list: readonly ReleaseHealth[],
  candidate: string | undefined,
  base: string | undefined,
): { candidate: ReleaseHealth | undefined; base: ReleaseHealth | undefined } {
  const ci = Math.max(
    0,
    list.findIndex((r) => r.release === candidate),
  )
  const bi = list.findIndex((r) => r.release === base)
  const cand = list[ci]
  if (!cand) return { candidate: undefined, base: undefined }
  return { candidate: cand, base: bi > ci ? list[bi] : list[ci + 1] }
}

/** After a click on the rail: the clicked release and the newer pick, ordered by age. */
export function repick(
  list: readonly ReleaseHealth[],
  pair: { candidate: string; base: string | undefined },
  clicked: string,
): { candidate: string; base: string | undefined } {
  if (clicked === pair.candidate || clicked === pair.base) return pair
  const index = (v: string | undefined) => list.findIndex((r) => r.release === v)
  const [newer, older] = [pair.candidate, clicked].sort((a, b) => index(a) - index(b))
  return { candidate: newer as string, base: older }
}
